import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckSquare, Square, Download, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSortable, SortTh, TableSearch } from './SortableTable';
import PhotoLightbox from './PhotoLightbox';
import { decodeConfig } from './DataTableNode';
import { sanitizeHtml } from '../lib/sanitizeHtml';

const fmtCell = (key, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (/starts_at|date_taken/.test(key) && /^\d{4}-\d{2}-\d{2}/.test(String(v))) { const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString([], { dateStyle: 'medium', timeStyle: String(v).length > 10 ? 'short' : undefined }); }
  return String(v);
};

// Live, interactive table: rows selected by tag, sortable column headers, row checkboxes, click a row to open it.
export function DataTable({ config }) {
  const { dataTableRows, mediaUrl } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const cfg = config || {};
  useEffect(() => {
    dataTableRows(cfg.source, cfg.tag).then(setData).catch((e) => setErr(e?.response?.status === 403 ? 'Sign in as a member to see this table.' : 'Could not load this table.'));
  }, [dataTableRows, cfg.source, cfg.tag]);
  const fields = (data ? data.fields : []).filter((f) => !cfg.fields || !cfg.fields.length || cfg.fields.includes(f.key));
  const fieldKeys = fields.map((f) => f.key);
  const { sorted, sort, toggle, query, setQuery } = useSortable((data && data.rows) || [], {
    initial: cfg.sortKey ? { key: cfg.sortKey, dir: 'asc', get: (r) => (r[cfg.sortKey] ?? '') } : null,
    searchFields: fieldKeys.map((k) => (r) => r[k]),
  });
  if (err) return <div className="my-3 text-sm px-3 py-2 rounded" style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--olive)' }} data-testid="data-table-error">{err}</div>;
  if (!data) return <div className="my-3 text-sm inline-flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={14} className="animate-spin" /> Loading table…</div>;
  const ids = sorted.map((r) => r.id);
  const allSel = ids.length > 0 && ids.every((id) => sel.includes(id));
  const open = (r, i) => { if (cfg.source === 'photos') setLightbox(i); else if (r.open) navigate(r.open); };
  const exportCsv = () => {
    const rows = sorted.filter((r) => !sel.length || sel.includes(r.id));
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [fields.map((f) => esc(f.label)).join(','), ...rows.map((r) => fields.map((f) => esc(r[f.key])).join(','))].join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = `${cfg.title || cfg.source}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <div className="my-4 not-prose data-table-live" data-testid={`data-table-${cfg.source}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div>
          {cfg.title && <h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>{cfg.title}</h3>}
          <span className="text-xs" style={{ color: 'var(--olive)' }} data-testid="data-table-meta">{sorted.length}{query ? ` of ${data.rows.length}` : ''} {cfg.sourceLabel || cfg.source}{cfg.tag ? ` tagged “${cfg.tag}”` : ''}{sel.length ? ` · ${sel.length} ticked` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          {(data.rows.length > 3 || query) && <TableSearch query={query} setQuery={setQuery} placeholder={`Filter ${cfg.sourceLabel || cfg.source}…`} testid="data-table-search" />}
          <button onClick={exportCsv} disabled={!sorted.length} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border disabled:opacity-40" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }} data-testid="data-table-export"><Download size={12} /> {sel.length ? `Export ${sel.length} ticked` : 'Export CSV'}</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded border bg-white" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
        <table className="w-full text-sm">
          <thead><tr style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--olive)' }}>
            <th className="px-2 py-2 w-8"><button onClick={() => setSel(allSel ? [] : ids)} title={allSel ? 'Untick all' : 'Tick all'} data-testid="data-table-select-all" className="flex">{allSel ? <CheckSquare size={15} /> : <Square size={15} />}</button></th>
            {fields.map((f) => <SortTh key={f.key} label={f.label} sortKey={f.key} get={(r) => (r[f.key] ?? '')} sort={sort} toggle={toggle} className="text-left px-2 py-2" testid={`data-table-th-${f.key}`} />)}
            <th className="w-8" />
          </tr></thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.id} onClick={() => open(r, i)} className="border-t cursor-pointer hover:bg-black/5" style={{ borderColor: 'rgba(0,0,0,0.06)', background: sel.includes(r.id) ? 'rgba(201,161,59,0.12)' : undefined }} data-testid={`data-table-row-${r.id}`}>
                <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.includes(r.id)} onChange={() => setSel((s) => (s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id]))} data-testid={`data-table-check-${r.id}`} /></td>
                {fields.map((f) => <td key={f.key} className="px-2 py-1.5 align-top" style={{ color: 'var(--ink)' }}>{fmtCell(f.key, r[f.key])}</td>)}
                <td className="px-2 py-1.5 text-right" style={{ color: 'var(--olive)' }}>{(cfg.source === 'photos' || r.open) && <ExternalLink size={13} />}</td>
              </tr>
            ))}
            {sorted.length === 0 && <tr><td colSpan={fields.length + 2} className="px-3 py-4 text-center text-xs" style={{ color: 'var(--olive)' }} data-testid="data-table-empty">{query ? `No rows match “${query}”.` : `Nothing to show${cfg.tag ? ` for tag “${cfg.tag}”` : ''}.`}</td></tr>}
          </tbody>
        </table>
      </div>
      {cfg.source === 'photos' && <PhotoLightbox items={sorted} index={lightbox} mediaUrl={mediaUrl} onClose={() => setLightbox(null)} onPrev={() => setLightbox((i) => (i - 1 + sorted.length) % sorted.length)} onNext={() => setLightbox((i) => (i + 1) % sorted.length)} />}
    </div>
  );
}

// Renders sanitised page HTML; every top-level <div data-datatable> placeholder becomes a live <DataTable>.
// The HTML is split around the placeholders so React owns the table (no DOM mutation / portals needed).
function splitHtml(safe) {
  if (!safe || !safe.includes('data-datatable')) return [{ html: safe }];
  const root = new DOMParser().parseFromString(`<div id="r">${safe}</div>`, 'text/html').getElementById('r');
  const parts = []; let buf = '';
  root.childNodes.forEach((n) => {
    if (n.nodeType === 1 && n.hasAttribute('data-datatable')) {
      if (buf) parts.push({ html: buf }); buf = '';
      const cfg = decodeConfig(n.getAttribute('data-datatable') || '');
      parts.push(cfg ? { cfg } : { html: n.outerHTML });
    } else buf += n.nodeType === 1 ? n.outerHTML : (n.textContent || '');
  });
  if (buf) parts.push({ html: buf });
  return parts;
}

export default function HtmlWithDataTables({ html, className = 'page-html', testId = 'page-html-content' }) {
  const safe = sanitizeHtml(html);
  const parts = useMemo(() => splitHtml(safe), [safe]);
  return (
    <div className={className} data-testid={testId}>
      {parts.map((p, i) => (p.cfg ? <DataTable key={`dt-${i}`} config={p.cfg} /> : <div key={`h-${i}`} dangerouslySetInnerHTML={{ __html: p.html }} />))}
    </div>
  );
}
